import React, { useState } from 'react';
import { apiClient } from '../../api/client';

export interface AnnouncementInfo {
  id: string;
  title: string;
  body: string;
  createdAt: string;
}

export interface AnnouncementsFeedProps {
  announcements: AnnouncementInfo[];
}

export function AnnouncementsFeed({ announcements }: AnnouncementsFeedProps) {
  const [dismissed, setDismissed] = useState<Set<string>>(new Set());

  const handleDismiss = async (id: string) => {
    setDismissed(prev => new Set(prev).add(id));
    try {
      await apiClient(`/announcements/${id}/dismiss`, { method: 'PATCH' });
    } catch {
      // Already hidden locally — non-critical if API fails
    }
  };

  const visible = announcements.filter(a => !dismissed.has(a.id)).slice(0, 5);

  return (
    <div style={styles.card}>
      <div style={styles.header}>Announcements</div>

      {visible.length === 0 ? (
        <div style={{ fontSize: '0.95rem', color: '#666' }}>
          {dismissed.size > 0 ? 'All caught up!' : 'No announcements at this time.'}
        </div>
      ) : (
        <div style={{ display: 'flex', flexDirection: 'column', gap: '0.75rem' }}>
          {visible.map((announcement) => (
            <div key={announcement.id} style={styles.item}>
              <div style={styles.itemContent}>
                <div style={styles.title}>{announcement.title}</div>
                <div style={styles.body} dangerouslySetInnerHTML={{ __html: announcement.body }} />
                <div style={styles.date}>
                  {new Date(announcement.createdAt).toLocaleDateString('en-US', {
                    month: 'short',
                    day: 'numeric',
                    year: 'numeric',
                  })}
                </div>
              </div>
              <button
                onClick={() => handleDismiss(announcement.id)}
                style={styles.dismissBtn}
                aria-label={`Dismiss ${announcement.title}`}
                title="Dismiss"
              >
                ✕
              </button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  card: {
    backgroundColor: '#fff',
    border: '1px solid #e0e0e0',
    borderRadius: '8px',
    boxShadow: '0 1px 3px rgba(0,0,0,0.06)',
    padding: '1.25rem',
  },
  header: {
    fontSize: '0.85rem',
    fontWeight: 600,
    color: '#1a6b3c',
    textTransform: 'uppercase' as const,
    letterSpacing: '0.5px',
    marginBottom: '0.75rem',
  },
  item: {
    display: 'flex',
    alignItems: 'flex-start',
    gap: '8px',
    borderBottom: '1px solid #f0f0f0',
    paddingBottom: '0.75rem',
  },
  itemContent: {
    flex: 1,
    minWidth: 0,
  },
  title: {
    fontSize: '0.95rem',
    fontWeight: 600,
    color: '#0f2d1f',
    marginBottom: '0.25rem',
  },
  body: {
    fontSize: '0.85rem',
    color: '#444',
    marginBottom: '0.35rem',
    lineHeight: 1.4,
  },
  date: {
    fontSize: '0.75rem',
    color: '#999',
  },
  dismissBtn: {
    background: 'none',
    border: 'none',
    cursor: 'pointer',
    fontSize: '0.85rem',
    color: '#999',
    padding: '4px 8px',
    borderRadius: '4px',
    minWidth: '32px',
    minHeight: '32px',
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    flexShrink: 0,
    transition: 'color 0.15s',
  },
};
